import { Capacitor } from '@capacitor/core';

/**
 * Gem-pack IAP purchase + restore (native only).
 *
 * <p><b>Native (iOS / Android):</b> RevenueCat — Apple IAP on iOS, Play Billing
 * on Android (Apple forbids billing digital goods any other way). RevenueCat is
 * <em>dynamically</em> imported so the web build doesn't require the plugin;
 * to activate native purchases:
 * <pre>
 *   pnpm --filter mobile add @revenuecat/purchases-capacitor
 *   npx cap sync
 *   # set VITE_REVENUECAT_IOS_KEY / VITE_REVENUECAT_ANDROID_KEY (public SDK keys)
 * </pre>
 * The RevenueCat webhook then credits the gem pack to the wallet server-side;
 * the client re-syncs the wallet after the purchase resolves.
 *
 * <p>There is no web purchase rail under free-to-play: on web (or a native
 * device with no RevenueCat key) {@link startPurchase} is a no-op.
 */

// `string`-typed (not a literal) so neither tsc nor Vite tries to resolve the
// uninstalled plugin at build — it's a pure runtime import, guarded below.
const RC_MODULE: string = '@revenuecat/purchases-capacitor';

const RC_IOS_KEY = import.meta.env.VITE_REVENUECAT_IOS_KEY as string | undefined;
const RC_ANDROID_KEY = import.meta.env.VITE_REVENUECAT_ANDROID_KEY as string | undefined;

function rcKey(): string | undefined {
  return Capacitor.getPlatform() === 'ios' ? RC_IOS_KEY : RC_ANDROID_KEY;
}

/** Start a gem-pack purchase. Resolves when the native flow completes; off-native
 *  (or with no RevenueCat key configured) it's a no-op — there is no web rail. */
export async function startPurchase(playerId: string): Promise<void> {
  if (!Capacitor.isNativePlatform() || !rcKey()) return;
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mod: any = await import(/* @vite-ignore */ RC_MODULE);
    const Purchases = mod.Purchases;
    await Purchases.configure({ apiKey: rcKey()!, appUserID: playerId });
    const offerings = await Purchases.getOfferings();
    const pkg = offerings?.current?.availablePackages?.[0];
    if (pkg) {
      await Purchases.purchasePackage({ aPackage: pkg });
      return;
    }
    console.warn('[billing] no RevenueCat offering available');
  } catch (e) {
    console.warn('[billing] RevenueCat purchase unavailable', e);
  }
}

/** Restore a prior App Store / Play purchase (Apple requires the button).
 *  Returns true if a restore ran; off-native it's a no-op. */
export async function restorePurchases(playerId: string): Promise<boolean> {
  if (!Capacitor.isNativePlatform() || !rcKey()) return false;
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mod: any = await import(/* @vite-ignore */ RC_MODULE);
    const Purchases = mod.Purchases;
    await Purchases.configure({ apiKey: rcKey()!, appUserID: playerId });
    await Purchases.restorePurchases();
    return true;
  } catch (e) {
    console.warn('[billing] restore failed', e);
    return false;
  }
}
